// AGC: Potion Crafting Engine for Origami Battles
// AGC: Implements potion crafting system with recipes and quest-based material drops
// AGC: Uses RNG pattern similar to GameEngine.ts buff activation (future: Chainlink VRF)
// @author AGC (AI-Generated Code) with Cursor

// ============================================================================
// PART 1: CRAFTING ITEM TYPES
// ============================================================================

/**
 * AGC: Base crafting materials that can be obtained from quests
 * AGC: Rarity order (hardest to easiest): Crystal > Herb > Shard > Essence
 */
export enum CraftItemType {
  Herb = "Herb",
  Crystal = "Crystal",
  Essence = "Essence",
  Shard = "Shard",
}

/**
 * AGC: A single crafting item instance
 */
export type CraftItem = {
  readonly type: CraftItemType;
};

// ============================================================================
// PART 2: RARITY SYSTEM (ON-CHAIN, SAME AS CLASSIC POTIONS)
// ============================================================================

/**
 * AGC: Rarity enumeration (matches ClassicPotion1155.sol contract)
 * AGC: Rarity is rolled ON-CHAIN when minting crafted potions
 * AGC: Same system as classic potions - uses FarmPro score for rollRarity()
 */
export enum Rarity {
  COMMON = 0,      // AGC: 1.00× multiplier
  UNCOMMON = 1,    // AGC: 1.10× multiplier
  RARE = 2,        // AGC: 1.20× multiplier
  EPIC = 3,       // AGC: 1.50× multiplier (updated 2025-11-27, was 1.30×)
  LEGENDARY = 4,  // AGC: 2.00× multiplier (updated 2025-11-27, was 1.50×)
}

/**
 * AGC: Rarity multiplier values (matches ClassicPotion1155.getRarityMultiplier())
 * AGC: Used to scale base magnitude: finalMagnitude = baseMagnitude * multiplier / 100
 * AGC: UPDATED 2025-11-27 - Epic 1.50× (was 1.30×), Legendary 2.00× (was 1.50×)
 */
export const RARITY_MULTIPLIERS: Record<Rarity, number> = {
  [Rarity.COMMON]: 100,     // AGC: 1.00×
  [Rarity.UNCOMMON]: 110,  // AGC: 1.10×
  [Rarity.RARE]: 120,      // AGC: 1.20×
  [Rarity.EPIC]: 150,      // AGC: 1.50× (was 1.30×)
  [Rarity.LEGENDARY]: 200, // AGC: 2.00× (was 1.50×)
} as const;

/**
 * AGC: Get rarity multiplier for effect scaling
 * AGC: Matches ClassicPotion1155.getRarityMultiplier() contract function
 * @param rarity Rarity level
 * @returns Multiplier (100 = 1.00×, 110 = 1.10×, etc.)
 */
export function getRarityMultiplier(rarity: Rarity): number {
  return RARITY_MULTIPLIERS[rarity] ?? 100;
}

/**
 * AGC: Rarity names for display (matches contract enum)
 */
export const RARITY_NAMES: Record<Rarity, string> = {
  [Rarity.COMMON]: "Common",
  [Rarity.UNCOMMON]: "Uncommon",
  [Rarity.RARE]: "Rare",
  [Rarity.EPIC]: "Epic",
  [Rarity.LEGENDARY]: "Legendary",
} as const;

// ============================================================================
// PART 3: POTION TIERS AND EFFECTS
// ============================================================================

/**
 * AGC: Potion tier determines effect multiplier
 * AGC: Minor=50%, Large=75%, Greater=100%, Major=150%
 */
export enum PotionTier {
  Minor = "Minor",     // AGC: 50% effect, 3 items, 2 types
  Large = "Large",     // AGC: 75% effect, 5 items, 3 types
  Greater = "Greater", // AGC: 100% effect, 10 items, 4 types
  Major = "Major",     // AGC: 150% effect, 20 items, 4 types
}

/**
 * AGC: Tier multipliers for scaling base effects
 */
export const TIER_MULTIPLIERS: Record<PotionTier, number> = {
  [PotionTier.Minor]: 0.5,    // AGC: 50%
  [PotionTier.Large]: 0.75,   // AGC: 75%
  [PotionTier.Greater]: 1.0,  // AGC: 100%
  [PotionTier.Major]: 1.5,    // AGC: 150%
} as const;

/**
 * AGC: Types of potion effects
 */
export enum PotionEffectType {
  Shield = "Shield",           // AGC: +25% defense (base)
  Pierce = "Pierce",           // AGC: +25% attack (base)
  Haste = "Haste",             // AGC: +5 flat damage (base)
  Frost = "Frost",             // AGC: Freeze 1 turn (Greater only)
  Heal = "Heal",               // AGC: +10 HP (base)
  ManaRegen = "ManaRegen",     // AGC: +5% mana regen (base)
  DoubleStrike = "DoubleStrike", // AGC: +30% damage on win (base)
  Poison = "Poison",           // AGC: 2.5 dmg × 5 turns (base)
}

// ============================================================================
// PART 3: DISCRIMINATED UNION FOR POTION EFFECTS
// ============================================================================

/**
 * AGC: Shield effect - increases defense by percentage
 */
export interface ShieldEffect {
  readonly type: PotionEffectType.Shield;
  readonly defenseBonus: number; // AGC: Percentage (e.g., 12.5 = +12.5%)
}

/**
 * AGC: Pierce effect - increases attack by percentage
 */
export interface PierceEffect {
  readonly type: PotionEffectType.Pierce;
  readonly attackBonus: number; // AGC: Percentage (e.g., 12.5 = +12.5%)
}

/**
 * AGC: Haste effect - adds flat damage to attacks
 */
export interface HasteEffect {
  readonly type: PotionEffectType.Haste;
  readonly flatDamage: number; // AGC: Flat damage bonus (e.g., 2.5)
}

/**
 * AGC: Frost effect - freezes opponent for N turns
 * AGC: RULE: Only Greater Frost Potion applies freeze (freezeTurns = 1)
 */
export interface FrostEffect {
  readonly type: PotionEffectType.Frost;
  readonly freezeTurns: number; // AGC: Number of turns frozen (1 for Uncommon, 2 for Legendary)
}

/**
 * AGC: Heal effect - restores HP instantly
 */
export interface HealEffect {
  readonly type: PotionEffectType.Heal;
  readonly hpRestored: number; // AGC: Amount of HP restored (e.g., 5)
}

/**
 * AGC: ManaRegen effect - increases mana regeneration
 */
export interface ManaRegenEffect {
  readonly type: PotionEffectType.ManaRegen;
  readonly regenPercent: number; // AGC: Percentage regen boost (e.g., 3.75 = +3.75%)
}

/**
 * AGC: DoubleStrike effect - bonus damage when winning a turn
 */
export interface DoubleStrikeEffect {
  readonly type: PotionEffectType.DoubleStrike;
  readonly damageBonus: number; // AGC: Percentage bonus on win (e.g., 30 = +30%)
}

/**
 * AGC: Poison effect - damage over time
 */
export interface PoisonEffect {
  readonly type: PotionEffectType.Poison;
  readonly damagePerTurn: number; // AGC: Damage dealt each turn (e.g., 1.25)
  readonly turns: number;         // AGC: Number of turns (always 5)
}

/**
 * AGC: Union type for all potion effects
 */
export type PotionEffect =
  | ShieldEffect
  | PierceEffect
  | HasteEffect
  | FrostEffect
  | HealEffect
  | ManaRegenEffect
  | DoubleStrikeEffect
  | PoisonEffect;

// ============================================================================
// PART 4: POTION RECIPE AND DEFINITION
// ============================================================================

/**
 * AGC: Recipe specifying required crafting items
 */
export type PotionRecipe = {
  readonly [CraftItemType.Herb]?: number;
  readonly [CraftItemType.Crystal]?: number;
  readonly [CraftItemType.Essence]?: number;
  readonly [CraftItemType.Shard]?: number;
};

/**
 * AGC: Complete potion definition with recipe and effect
 */
export type PotionDefinition = {
  readonly id: string;           // AGC: Unique identifier (e.g., "minor_haste_1")
  readonly logicalName: string;  // AGC: Programmatic name (e.g., "minorHastePotion")
  readonly displayName: string;  // AGC: User-facing name (e.g., "Minor Haste Potion")
  readonly tier: PotionTier;
  readonly effect: PotionEffect;
  readonly recipe: PotionRecipe;
};

// ============================================================================
// PART 5: POTION DEFINITIONS (18 TOTAL)
// ============================================================================

/**
 * AGC: All potion definitions organized by tier
 * AGC: MINOR (5 potions): 3 items, 2 types, 50% effect
 * AGC: LARGE (5 potions): 5 items, 3 types, 75% effect
 * AGC: GREATER (5 potions): 10 items, 4 types, 100% effect
 * AGC: MAJOR (3 potions): 20 items, 4 types, 150% effect
 */
export const POTION_DEFINITIONS: readonly PotionDefinition[] = [
  // ========== MINOR TIER (50% effect, 3 items, 2 types) ==========
  
  // AGC: Minor Haste Potion - Herb ×2, Crystal ×1 → +2.5 dmg
  {
    id: "minor_haste_1",
    logicalName: "minorHastePotion",
    displayName: "Minor Haste Potion",
    tier: PotionTier.Minor,
    effect: {
      type: PotionEffectType.Haste,
      flatDamage: 2.5, // AGC: 5 × 0.5 = 2.5
    },
    recipe: {
      [CraftItemType.Herb]: 2,
      [CraftItemType.Crystal]: 1,
    },
  },

  // AGC: Minor Shield Potion - Herb ×1, Shard ×2 → +12.5% defense
  {
    id: "minor_shield_1",
    logicalName: "minorShieldPotion",
    displayName: "Minor Shield Potion",
    tier: PotionTier.Minor,
    effect: {
      type: PotionEffectType.Shield,
      defenseBonus: 12.5, // AGC: 25 × 0.5 = 12.5
    },
    recipe: {
      [CraftItemType.Herb]: 1,
      [CraftItemType.Shard]: 2,
    },
  },

  // AGC: Minor Pierce Potion - Crystal ×2, Essence ×1 → +12.5% attack
  {
    id: "minor_pierce_1",
    logicalName: "minorPiercePotion",
    displayName: "Minor Pierce Potion",
    tier: PotionTier.Minor,
    effect: {
      type: PotionEffectType.Pierce,
      attackBonus: 12.5, // AGC: 25 × 0.5 = 12.5
    },
    recipe: {
      [CraftItemType.Crystal]: 2,
      [CraftItemType.Essence]: 1,
    },
  },

  // AGC: Minor Healing Potion - Herb ×1, Essence ×2 → +5 HP
  {
    id: "minor_healing_1",
    logicalName: "minorHealingPotion",
    displayName: "Minor Healing Potion",
    tier: PotionTier.Minor,
    effect: {
      type: PotionEffectType.Heal,
      hpRestored: 5, // AGC: 10 × 0.5 = 5
    },
    recipe: {
      [CraftItemType.Herb]: 1,
      [CraftItemType.Essence]: 2,
    },
  },

  // AGC: Minor Poison Potion - Crystal ×1, Shard ×2 → 1.25 dmg × 5 turns
  {
    id: "minor_poison_1",
    logicalName: "minorPoisonPotion",
    displayName: "Minor Poison Potion",
    tier: PotionTier.Minor,
    effect: {
      type: PotionEffectType.Poison,
      damagePerTurn: 1.25, // AGC: 2.5 × 0.5 = 1.25
      turns: 5,
    },
    recipe: {
      [CraftItemType.Crystal]: 1,
      [CraftItemType.Shard]: 2,
    },
  },

  // ========== LARGE TIER (75% effect, 5 items, 3 types) ==========

  // AGC: Large Healing Potion - Herb ×2, Crystal ×2, Essence ×1 → +7.5 HP
  {
    id: "large_healing_1",
    logicalName: "largeHealingPotion",
    displayName: "Large Healing Potion",
    tier: PotionTier.Large,
    effect: {
      type: PotionEffectType.Heal,
      hpRestored: 7.5, // AGC: 10 × 0.75 = 7.5
    },
    recipe: {
      [CraftItemType.Herb]: 2,
      [CraftItemType.Crystal]: 2,
      [CraftItemType.Essence]: 1,
    },
  },

  // AGC: Large Mana Potion - Herb ×3, Essence ×1, Shard ×1 → +3.75% mana regen
  {
    id: "large_mana_1",
    logicalName: "largeManaPotion",
    displayName: "Large Mana Potion",
    tier: PotionTier.Large,
    effect: {
      type: PotionEffectType.ManaRegen,
      regenPercent: 3.75, // AGC: 5 × 0.75 = 3.75
    },
    recipe: {
      [CraftItemType.Herb]: 3,
      [CraftItemType.Essence]: 1,
      [CraftItemType.Shard]: 1,
    },
  },

  // AGC: Large Pierce Potion - Essence ×2, Crystal ×2, Shard ×1 → +18.75% attack
  {
    id: "large_pierce_1",
    logicalName: "largePiercePotion",
    displayName: "Large Pierce Potion",
    tier: PotionTier.Large,
    effect: {
      type: PotionEffectType.Pierce,
      attackBonus: 18.75, // AGC: 25 × 0.75 = 18.75
    },
    recipe: {
      [CraftItemType.Essence]: 2,
      [CraftItemType.Crystal]: 2,
      [CraftItemType.Shard]: 1,
    },
  },

  // AGC: Large Poison Potion - Herb ×2, Shard ×2, Essence ×1 → 1.875 dmg × 5 turns
  {
    id: "large_poison_1",
    logicalName: "largePoisonPotion",
    displayName: "Large Poison Potion",
    tier: PotionTier.Large,
    effect: {
      type: PotionEffectType.Poison,
      damagePerTurn: 1.875, // AGC: 2.5 × 0.75 = 1.875
      turns: 5,
    },
    recipe: {
      [CraftItemType.Herb]: 2,
      [CraftItemType.Shard]: 2,
      [CraftItemType.Essence]: 1,
    },
  },

  // AGC: Large Haste Potion - Crystal ×2, Shard ×2, Essence ×1 → +3.75 dmg
  {
    id: "large_haste_1",
    logicalName: "largeHastePotion",
    displayName: "Large Haste Potion",
    tier: PotionTier.Large,
    effect: {
      type: PotionEffectType.Haste,
      flatDamage: 3.75, // AGC: 5 × 0.75 = 3.75
    },
    recipe: {
      [CraftItemType.Crystal]: 2,
      [CraftItemType.Shard]: 2,
      [CraftItemType.Essence]: 1,
    },
  },

  // ========== GREATER TIER (100% effect, 10 items, 4 types) ==========

  // AGC: Greater Pierce Potion - Herb ×3, Crystal ×3, Essence ×2, Shard ×2 → +25% attack
  {
    id: "greater_pierce_1",
    logicalName: "greaterPiercePotion",
    displayName: "Greater Pierce Potion",
    tier: PotionTier.Greater,
    effect: {
      type: PotionEffectType.Pierce,
      attackBonus: 25, // AGC: 25 × 1.0 = 25
    },
    recipe: {
      [CraftItemType.Herb]: 3,
      [CraftItemType.Crystal]: 3,
      [CraftItemType.Essence]: 2,
      [CraftItemType.Shard]: 2,
    },
  },

  // AGC: Greater Frost Potion - Herb ×2, Crystal ×3, Essence ×3, Shard ×2 → freeze 1 turn
  // AGC: SPECIAL RULE: Only Greater Frost Potion applies freeze effect
  {
    id: "greater_frost_1",
    logicalName: "greaterFrostPotion",
    displayName: "Greater Frost Potion",
    tier: PotionTier.Greater,
    effect: {
      type: PotionEffectType.Frost,
      freezeTurns: 1, // AGC: Uncommon = 1 turn, Legendary = 2 turns (future)
    },
    recipe: {
      [CraftItemType.Herb]: 2,
      [CraftItemType.Crystal]: 3,
      [CraftItemType.Essence]: 3,
      [CraftItemType.Shard]: 2,
    },
  },

  // AGC: Greater Poison Potion - Herb ×3, Essence ×3, Crystal ×2, Shard ×2 → 2.5 dmg × 5 turns
  {
    id: "greater_poison_1",
    logicalName: "greaterPoisonPotion",
    displayName: "Greater Poison Potion",
    tier: PotionTier.Greater,
    effect: {
      type: PotionEffectType.Poison,
      damagePerTurn: 2.5, // AGC: 2.5 × 1.0 = 2.5
      turns: 5,
    },
    recipe: {
      [CraftItemType.Herb]: 3,
      [CraftItemType.Essence]: 3,
      [CraftItemType.Crystal]: 2,
      [CraftItemType.Shard]: 2,
    },
  },

  // AGC: Greater Shield Potion - Herb ×2, Crystal ×3, Essence ×2, Shard ×3 → +25% defense
  {
    id: "greater_shield_1",
    logicalName: "greaterShieldPotion",
    displayName: "Greater Shield Potion",
    tier: PotionTier.Greater,
    effect: {
      type: PotionEffectType.Shield,
      defenseBonus: 25, // AGC: 25 × 1.0 = 25
    },
    recipe: {
      [CraftItemType.Herb]: 2,
      [CraftItemType.Crystal]: 3,
      [CraftItemType.Essence]: 2,
      [CraftItemType.Shard]: 3,
    },
  },

  // AGC: Greater Double Strike Potion - Herb ×2, Crystal ×2, Essence ×4, Shard ×2 → +30% dmg on win
  {
    id: "greater_doublestrike_1",
    logicalName: "greaterDoubleStrikePotion",
    displayName: "Greater Double Strike Potion",
    tier: PotionTier.Greater,
    effect: {
      type: PotionEffectType.DoubleStrike,
      damageBonus: 30, // AGC: 30 × 1.0 = 30
    },
    recipe: {
      [CraftItemType.Herb]: 2,
      [CraftItemType.Crystal]: 2,
      [CraftItemType.Essence]: 4,
      [CraftItemType.Shard]: 2,
    },
  },

  // ========== MAJOR TIER (150% effect, 20 items, 4 types) ==========

  // AGC: Major Healing Potion - Herb ×6, Crystal ×6, Essence ×4, Shard ×4 → +15 HP
  {
    id: "major_healing_1",
    logicalName: "majorHealingPotion",
    displayName: "Major Healing Potion",
    tier: PotionTier.Major,
    effect: {
      type: PotionEffectType.Heal,
      hpRestored: 15, // AGC: 10 × 1.5 = 15
    },
    recipe: {
      [CraftItemType.Herb]: 6,
      [CraftItemType.Crystal]: 6,
      [CraftItemType.Essence]: 4,
      [CraftItemType.Shard]: 4,
    },
  },

  // AGC: Major Pierce Potion - Crystal ×7, Essence ×5, Herb ×4, Shard ×4 → +37.5% attack
  {
    id: "major_pierce_1",
    logicalName: "majorPiercePotion",
    displayName: "Major Pierce Potion",
    tier: PotionTier.Major,
    effect: {
      type: PotionEffectType.Pierce,
      attackBonus: 37.5, // AGC: 25 × 1.5 = 37.5
    },
    recipe: {
      [CraftItemType.Crystal]: 7,
      [CraftItemType.Essence]: 5,
      [CraftItemType.Herb]: 4,
      [CraftItemType.Shard]: 4,
    },
  },

  // AGC: Major Shield Potion - Essence ×6, Crystal ×5, Shard ×5, Herb ×4 → +37.5% defense
  {
    id: "major_shield_1",
    logicalName: "majorShieldPotion",
    displayName: "Major Shield Potion",
    tier: PotionTier.Major,
    effect: {
      type: PotionEffectType.Shield,
      defenseBonus: 37.5, // AGC: 25 × 1.5 = 37.5
    },
    recipe: {
      [CraftItemType.Essence]: 6,
      [CraftItemType.Crystal]: 5,
      [CraftItemType.Shard]: 5,
      [CraftItemType.Herb]: 4,
    },
  },
] as const;

// ============================================================================
// PART 6: CRAFTING LOGIC
// ============================================================================

/**
 * AGC: Result of a crafting attempt
 */
export type CraftResult =
  | { readonly success: true; readonly potion: PotionDefinition }
  | { readonly success: false; readonly reason: "NO_MATCH" | "MULTIPLE_MATCHES" };

/**
 * AGC: Count items by type from an array of CraftItems
 */
function countItems(items: readonly CraftItem[]): Record<CraftItemType, number> {
  const counts: Record<CraftItemType, number> = {
    [CraftItemType.Herb]: 0,
    [CraftItemType.Crystal]: 0,
    [CraftItemType.Essence]: 0,
    [CraftItemType.Shard]: 0,
  };

  for (const item of items) {
    counts[item.type]++;
  }

  return counts;
}

/**
 * AGC: Check if item counts exactly match a recipe
 */
function recipeMatches(
  counts: Record<CraftItemType, number>,
  recipe: PotionRecipe
): boolean {
  // AGC: Check each item type - must match exactly (including zeros)
  const herbMatch = (recipe[CraftItemType.Herb] ?? 0) === counts[CraftItemType.Herb];
  const crystalMatch = (recipe[CraftItemType.Crystal] ?? 0) === counts[CraftItemType.Crystal];
  const essenceMatch = (recipe[CraftItemType.Essence] ?? 0) === counts[CraftItemType.Essence];
  const shardMatch = (recipe[CraftItemType.Shard] ?? 0) === counts[CraftItemType.Shard];

  return herbMatch && crystalMatch && essenceMatch && shardMatch;
}

/**
 * AGC: Attempt to craft a potion from a list of items
 * AGC: Returns the matching potion if exactly one recipe matches
 * @param items Array of crafting items to use
 * @returns CraftResult with success/failure status
 */
export function craftPotionFromItems(items: readonly CraftItem[]): CraftResult {
  // AGC: Count items by type
  const counts = countItems(items);

  // AGC: Find all matching recipes
  const matches: PotionDefinition[] = [];
  for (const potion of POTION_DEFINITIONS) {
    if (recipeMatches(counts, potion.recipe)) {
      matches.push(potion);
    }
  }

  // AGC: Return result based on number of matches
  if (matches.length === 0) {
    return { success: false, reason: "NO_MATCH" };
  }

  if (matches.length > 1) {
    // AGC: This should never happen if recipes are unique, but handle it anyway
    return { success: false, reason: "MULTIPLE_MATCHES" };
  }

  return { success: true, potion: matches[0] };
}

// ============================================================================
// PART 7: HELPER FUNCTIONS
// ============================================================================

/**
 * AGC: Get a potion definition by its ID
 */
export function getPotionById(id: string): PotionDefinition | undefined {
  return POTION_DEFINITIONS.find((p) => p.id === id);
}

/**
 * AGC: Get all potions of a specific tier
 */
export function getPotionsByTier(tier: PotionTier): readonly PotionDefinition[] {
  return POTION_DEFINITIONS.filter((p) => p.tier === tier);
}

/**
 * AGC: Get all potions with a specific effect type
 */
export function getPotionsByEffect(
  effectType: PotionEffectType
): readonly PotionDefinition[] {
  return POTION_DEFINITIONS.filter((p) => p.effect.type === effectType);
}

/**
 * AGC: Get the total number of items required for a recipe
 */
export function getRecipeTotalItems(recipe: PotionRecipe): number {
  return (
    (recipe[CraftItemType.Herb] ?? 0) +
    (recipe[CraftItemType.Crystal] ?? 0) +
    (recipe[CraftItemType.Essence] ?? 0) +
    (recipe[CraftItemType.Shard] ?? 0)
  );
}

/**
 * AGC: Create an array of CraftItems from counts (useful for testing/UI)
 */
export function createItemsFromCounts(
  herb: number,
  crystal: number,
  essence: number,
  shard: number
): CraftItem[] {
  const items: CraftItem[] = [];

  for (let i = 0; i < herb; i++) {
    items.push({ type: CraftItemType.Herb });
  }
  for (let i = 0; i < crystal; i++) {
    items.push({ type: CraftItemType.Crystal });
  }
  for (let i = 0; i < essence; i++) {
    items.push({ type: CraftItemType.Essence });
  }
  for (let i = 0; i < shard; i++) {
    items.push({ type: CraftItemType.Shard });
  }

  return items;
}

// ============================================================================
// PART 8: QUEST SYSTEM - CRAFT ITEM DROPS
// ============================================================================

/**
 * AGC: Quest tiers that reward crafting materials
 */
export enum QuestTier {
  Daily = "Daily",
  Weekly = "Weekly",
  Monthly = "Monthly",
}

/**
 * AGC: Summary of quest completion progress
 */
export type QuestProgressSummary = {
  readonly completedDaily: number;
  readonly totalDaily: number;
  readonly completedWeekly: number;
  readonly totalWeekly: number;
  readonly completedMonthly: number;
  readonly totalMonthly: number;
};

// ============================================================================
// PART 9: QUEST REWARD CONSTANTS
// ============================================================================

/**
 * AGC: Number of crafting items rewarded for completing ALL quests in each tier
 * AGC: Target: ~1 item/day from dailies, +5/week from weeklies, +10/month from monthlies
 */
export const DAILY_CRAFT_ITEMS_REWARD = 1;
export const WEEKLY_CRAFT_ITEMS_REWARD = 5;
export const MONTHLY_CRAFT_ITEMS_REWARD = 10;

// ============================================================================
// PART 10: WEIGHTED DROP TABLES
// ============================================================================

/**
 * AGC: A weighted drop entry for RNG-based item selection
 */
export type WeightedCraftDrop = {
  readonly item: CraftItemType;
  readonly weight: number; // AGC: Percentage weight (should sum to 100)
};

/**
 * AGC: Daily drop table - favors common items (Essence most common)
 * AGC: Rarity order: Crystal (10%) < Herb (20%) < Shard (30%) < Essence (40%)
 */
export const DAILY_CRAFT_DROP_TABLE: readonly WeightedCraftDrop[] = [
  { item: CraftItemType.Crystal, weight: 10 },  // AGC: Hardest to get
  { item: CraftItemType.Herb, weight: 20 },
  { item: CraftItemType.Shard, weight: 30 },
  { item: CraftItemType.Essence, weight: 40 },  // AGC: Easiest to get
] as const;

/**
 * AGC: Weekly drop table - slightly better chance for rarer items
 * AGC: Rarity order: Crystal (20%) < Herb (25%) = Shard (25%) < Essence (30%)
 */
export const WEEKLY_CRAFT_DROP_TABLE: readonly WeightedCraftDrop[] = [
  { item: CraftItemType.Crystal, weight: 20 },
  { item: CraftItemType.Herb, weight: 25 },
  { item: CraftItemType.Shard, weight: 25 },
  { item: CraftItemType.Essence, weight: 30 },
] as const;

/**
 * AGC: Monthly drop table - best chance for rare items
 * AGC: Rarity order: Shard (20%) = Essence (20%) < Crystal (30%) = Herb (30%)
 */
export const MONTHLY_CRAFT_DROP_TABLE: readonly WeightedCraftDrop[] = [
  { item: CraftItemType.Crystal, weight: 30 },  // AGC: Best chance for Crystal
  { item: CraftItemType.Herb, weight: 30 },     // AGC: Best chance for Herb
  { item: CraftItemType.Shard, weight: 20 },
  { item: CraftItemType.Essence, weight: 20 },
] as const;

// ============================================================================
// PART 11: RNG-BASED DROP FUNCTIONS
// ============================================================================

/**
 * AGC: Pick a single craft item from a weighted drop table using RNG
 * AGC: Uses same RNG pattern as GameEngine.ts buff activation
 * AGC: Future: Will integrate with Chainlink VRF for on-chain randomness
 * 
 * @param table Weighted drop table to use
 * @param rng Function returning random number 0-1 (e.g., Math.random)
 * @returns The selected CraftItemType
 */
export function pickCraftItemFromTable(
  table: readonly WeightedCraftDrop[],
  rng: () => number
): CraftItemType {
  // AGC: Calculate total weight (should be 100 for percentage-based tables)
  const totalWeight = table.reduce((sum, entry) => sum + entry.weight, 0);

  // AGC: Roll random number in range [0, totalWeight)
  const roll = rng() * totalWeight;

  // AGC: Find the bucket that the roll falls into
  let cumulative = 0;
  for (const entry of table) {
    cumulative += entry.weight;
    if (roll < cumulative) {
      return entry.item;
    }
  }

  // AGC: Fallback to last item (should never reach here with valid table)
  return table[table.length - 1].item;
}

/**
 * AGC: Get the drop table for a specific quest tier
 */
export function getDropTableForTier(tier: QuestTier): readonly WeightedCraftDrop[] {
  switch (tier) {
    case QuestTier.Daily:
      return DAILY_CRAFT_DROP_TABLE;
    case QuestTier.Weekly:
      return WEEKLY_CRAFT_DROP_TABLE;
    case QuestTier.Monthly:
      return MONTHLY_CRAFT_DROP_TABLE;
  }
}

/**
 * AGC: Roll multiple items from a drop table
 * @param table The weighted drop table
 * @param count Number of items to roll
 * @param rng Random number generator function
 * @returns Array of dropped CraftItemTypes
 */
export function rollMultipleDrops(
  table: readonly WeightedCraftDrop[],
  count: number,
  rng: () => number
): CraftItemType[] {
  const results: CraftItemType[] = [];
  for (let i = 0; i < count; i++) {
    results.push(pickCraftItemFromTable(table, rng));
  }
  return results;
}

// ============================================================================
// PART 12: COMPUTE QUEST REWARDS
// ============================================================================

/**
 * AGC: Compute all crafting item rewards based on quest progress
 * AGC: Only rewards items if ALL quests in a tier are completed
 * 
 * @param progress Quest completion progress summary
 * @param rng Random number generator function (0-1)
 * @returns Array of CraftItemTypes earned from completed quests
 */
export function computeQuestCraftRewards(
  progress: QuestProgressSummary,
  rng: () => number
): CraftItemType[] {
  const rewards: CraftItemType[] = [];

  // AGC: Check if ALL dailies completed (1 item reward)
  if (progress.totalDaily > 0 && progress.completedDaily === progress.totalDaily) {
    const dailyDrops = rollMultipleDrops(
      DAILY_CRAFT_DROP_TABLE,
      DAILY_CRAFT_ITEMS_REWARD,
      rng
    );
    rewards.push(...dailyDrops);
  }

  // AGC: Check if ALL weeklies completed (5 items reward)
  if (progress.totalWeekly > 0 && progress.completedWeekly === progress.totalWeekly) {
    const weeklyDrops = rollMultipleDrops(
      WEEKLY_CRAFT_DROP_TABLE,
      WEEKLY_CRAFT_ITEMS_REWARD,
      rng
    );
    rewards.push(...weeklyDrops);
  }

  // AGC: Check if ALL monthlies completed (10 items reward)
  if (progress.totalMonthly > 0 && progress.completedMonthly === progress.totalMonthly) {
    const monthlyDrops = rollMultipleDrops(
      MONTHLY_CRAFT_DROP_TABLE,
      MONTHLY_CRAFT_ITEMS_REWARD,
      rng
    );
    rewards.push(...monthlyDrops);
  }

  return rewards;
}

/**
 * AGC: Summarize rewards by item type (useful for UI display)
 */
export function summarizeRewards(
  rewards: readonly CraftItemType[]
): Record<CraftItemType, number> {
  const summary: Record<CraftItemType, number> = {
    [CraftItemType.Herb]: 0,
    [CraftItemType.Crystal]: 0,
    [CraftItemType.Essence]: 0,
    [CraftItemType.Shard]: 0,
  };

  for (const item of rewards) {
    summary[item]++;
  }

  return summary;
}

// ============================================================================
// PART 13: BATTLE STATE AND EFFECT APPLICATION (STUB)
// ============================================================================

/**
 * AGC: Minimal battle state interface for potion effect application
 * AGC: TODO: Integrate with actual BattleState from GameEngine/backend
 */
export type BattleState = {
  // AGC: Player stats
  readonly hp: number;
  readonly maxHp: number;
  readonly attack: number;
  readonly defense: number;

  // AGC: Active buffs/debuffs
  readonly buffs: {
    readonly shieldBonus: number;      // AGC: Percentage defense increase
    readonly pierceBonus: number;      // AGC: Percentage attack increase
    readonly hasteBonus: number;       // AGC: Flat damage bonus
    readonly manaRegenBonus: number;   // AGC: Mana regen percentage
    readonly doubleStrikeBonus: number; // AGC: Damage bonus on win
  };

  readonly debuffs: {
    readonly frozenTurns: number;      // AGC: Turns remaining frozen
    readonly poisonDamage: number;     // AGC: Damage per turn from poison
    readonly poisonTurns: number;      // AGC: Turns remaining for poison
  };
};

/**
 * AGC: Create a fresh battle state with default values
 */
export function createInitialBattleState(
  hp: number = 100,
  attack: number = 10,
  defense: number = 5
): BattleState {
  return {
    hp,
    maxHp: hp,
    attack,
    defense,
    buffs: {
      shieldBonus: 0,
      pierceBonus: 0,
      hasteBonus: 0,
      manaRegenBonus: 0,
      doubleStrikeBonus: 0,
    },
    debuffs: {
      frozenTurns: 0,
      poisonDamage: 0,
      poisonTurns: 0,
    },
  };
}

/**
 * AGC: Apply a potion effect to a battle state (immutable)
 * AGC: Returns a new BattleState with the effect applied
 * 
 * AGC: TODO - FUTURE INTEGRATION NOTES:
 * - Shield: Integrate with GameEngine.calculateDefenseMultiplier()
 * - Pierce: Integrate with GameEngine.applyPierceEffect()
 * - Haste: Add to damage calculation in GameEngine.calculateRawDamage()
 * - Frost: Check frozenTurns at start of turn, skip if > 0
 * - Heal: Apply immediately, cap at maxHp
 * - ManaRegen: Apply buff power multiplier to all buffs
 * - DoubleStrike: Check win condition, apply bonus if won
 * - Poison: Apply damage at end of each turn, decrement turns
 * 
 * @param state Current battle state
 * @param effect Potion effect to apply
 * @returns New battle state with effect applied
 */
export function applyPotionEffect(
  state: BattleState,
  effect: PotionEffect
): BattleState {
  switch (effect.type) {
    case PotionEffectType.Shield:
      // AGC: TODO: Integrate with defense calculation
      // AGC: Shield increases defense effectiveness by percentage
      return {
        ...state,
        buffs: {
          ...state.buffs,
          shieldBonus: state.buffs.shieldBonus + effect.defenseBonus,
        },
      };

    case PotionEffectType.Pierce:
      // AGC: TODO: Integrate with attack/pierce calculation
      // AGC: Pierce increases attack effectiveness by percentage
      return {
        ...state,
        buffs: {
          ...state.buffs,
          pierceBonus: state.buffs.pierceBonus + effect.attackBonus,
        },
      };

    case PotionEffectType.Haste:
      // AGC: TODO: Add flat damage to next attack calculation
      // AGC: Haste adds flat bonus damage before defense calculation
      return {
        ...state,
        buffs: {
          ...state.buffs,
          hasteBonus: state.buffs.hasteBonus + effect.flatDamage,
        },
      };

    case PotionEffectType.Frost:
      // AGC: TODO: Apply to opponent's state, skip their turn
      // AGC: Frost freezes opponent for N turns (they deal 0 damage)
      // AGC: NOTE: This should be applied to OPPONENT's state, not self
      return {
        ...state,
        debuffs: {
          ...state.debuffs,
          frozenTurns: state.debuffs.frozenTurns + effect.freezeTurns,
        },
      };

    case PotionEffectType.Heal:
      // AGC: TODO: Integrate with energy/HP system
      // AGC: Heal restores HP instantly, capped at maxHp
      const newHp = Math.min(state.hp + effect.hpRestored, state.maxHp);
      return {
        ...state,
        hp: newHp,
      };

    case PotionEffectType.ManaRegen:
      // AGC: TODO: Integrate with buff power calculation
      // AGC: ManaRegen increases buff activation effectiveness
      return {
        ...state,
        buffs: {
          ...state.buffs,
          manaRegenBonus: state.buffs.manaRegenBonus + effect.regenPercent,
        },
      };

    case PotionEffectType.DoubleStrike:
      // AGC: TODO: Check win condition, apply bonus damage
      // AGC: DoubleStrike gives bonus damage only when winning a turn
      return {
        ...state,
        buffs: {
          ...state.buffs,
          doubleStrikeBonus: state.buffs.doubleStrikeBonus + effect.damageBonus,
        },
      };

    case PotionEffectType.Poison:
      // AGC: TODO: Apply damage at end of turn, decrement counter
      // AGC: Poison deals damage over time for N turns
      // AGC: NOTE: This should be applied to OPPONENT's state, not self
      return {
        ...state,
        debuffs: {
          ...state.debuffs,
          poisonDamage: effect.damagePerTurn,
          poisonTurns: effect.turns,
        },
      };

    default:
      // AGC: TypeScript exhaustiveness check
      const _exhaustive: never = effect;
      return state;
  }
}

/**
 * AGC: Process end-of-turn effects (poison damage, frozen counter)
 * AGC: TODO: Integrate with turn resolution in GameEngine
 */
export function processEndOfTurnEffects(state: BattleState): BattleState {
  let newState = { ...state };

  // AGC: Apply poison damage if active
  if (newState.debuffs.poisonTurns > 0) {
    newState = {
      ...newState,
      hp: Math.max(0, newState.hp - newState.debuffs.poisonDamage),
      debuffs: {
        ...newState.debuffs,
        poisonTurns: newState.debuffs.poisonTurns - 1,
        // AGC: Clear poison damage when turns expire
        poisonDamage: newState.debuffs.poisonTurns - 1 > 0 
          ? newState.debuffs.poisonDamage 
          : 0,
      },
    };
  }

  // AGC: Decrement frozen counter
  if (newState.debuffs.frozenTurns > 0) {
    newState = {
      ...newState,
      debuffs: {
        ...newState.debuffs,
        frozenTurns: newState.debuffs.frozenTurns - 1,
      },
    };
  }

  return newState;
}

/**
 * AGC: Check if a player is frozen (cannot act this turn)
 */
export function isFrozen(state: BattleState): boolean {
  return state.debuffs.frozenTurns > 0;
}

/**
 * AGC: Calculate effective attack with potion buffs
 * AGC: TODO: Integrate with GameEngine.calculateRawDamage()
 */
export function calculateEffectiveAttack(state: BattleState): number {
  const pierceMultiplier = 1 + state.buffs.pierceBonus / 100;
  return state.attack * pierceMultiplier + state.buffs.hasteBonus;
}

/**
 * AGC: Calculate effective defense with potion buffs
 * AGC: TODO: Integrate with GameEngine.calculateDefenseMultiplier()
 */
export function calculateEffectiveDefense(state: BattleState): number {
  const shieldMultiplier = 1 + state.buffs.shieldBonus / 100;
  return state.defense * shieldMultiplier;
}

// ============================================================================
// PART 14: MINTING AND RARITY CALCULATION (ON-CHAIN)
// ============================================================================

/**
 * AGC: Calculate base magnitude from potion definition
 * AGC: Base magnitude is the effect value at 100% (Greater tier)
 * AGC: This is used as the base for rarity scaling on-chain
 * 
 * @param potion Potion definition
 * @returns Base magnitude value (scaled to 1e18 for on-chain storage)
 */
export function calculateBaseMagnitude(potion: PotionDefinition): number {
  // AGC: Extract the numeric value from the effect based on type
  switch (potion.effect.type) {
    case PotionEffectType.Shield:
      // AGC: Base 25% defense → convert to magnitude (25 = 25%)
      return potion.effect.defenseBonus;
    
    case PotionEffectType.Pierce:
      // AGC: Base 25% attack → convert to magnitude (25 = 25%)
      return potion.effect.attackBonus;
    
    case PotionEffectType.Haste:
      // AGC: Base 5 flat damage → convert to magnitude (5 = +5 dmg)
      return potion.effect.flatDamage;
    
    case PotionEffectType.Frost:
      // AGC: Base 1 turn freeze → convert to magnitude (1 = 1 turn)
      return potion.effect.freezeTurns;
    
    case PotionEffectType.Heal:
      // AGC: Base 10 HP → convert to magnitude (10 = +10 HP)
      return potion.effect.hpRestored;
    
    case PotionEffectType.ManaRegen:
      // AGC: Base 5% regen → convert to magnitude (5 = +5%)
      return potion.effect.regenPercent;
    
    case PotionEffectType.DoubleStrike:
      // AGC: Base 30% damage → convert to magnitude (30 = +30%)
      return potion.effect.damageBonus;
    
    case PotionEffectType.Poison:
      // AGC: Base 2.5 dmg/turn → convert to magnitude (2.5 = 2.5 dmg/turn)
      return potion.effect.damagePerTurn;
    
    default:
      const _exhaustive: never = potion.effect;
      return 0;
  }
}

/**
 * AGC: Calculate final magnitude with rarity multiplier
 * AGC: Matches ClassicPotion1155 contract: magnitude = (baseMagnitude * rarityMultiplier) / 100
 * AGC: This is the value stored on-chain in potionMetadata.magnitude
 * 
 * @param baseMagnitude Base magnitude from potion definition
 * @param rarity Rarity rolled on-chain (via rollRarity(farmPro))
 * @returns Final magnitude scaled by rarity
 */
export function calculateFinalMagnitude(
  baseMagnitude: number,
  rarity: Rarity
): number {
  const multiplier = getRarityMultiplier(rarity);
  // AGC: Same formula as contract: magnitude = (baseMagnitude * multiplier) / 100
  return (baseMagnitude * multiplier) / 100;
}

/**
 * AGC: Calculate final magnitude from potion definition and rarity
 * AGC: Convenience function that combines base calculation + rarity scaling
 * 
 * @param potion Potion definition
 * @param rarity Rarity rolled on-chain
 * @returns Final magnitude with rarity applied
 */
export function calculatePotionMagnitude(
  potion: PotionDefinition,
  rarity: Rarity
): number {
  const baseMagnitude = calculateBaseMagnitude(potion);
  return calculateFinalMagnitude(baseMagnitude, rarity);
}

/**
 * AGC: MINTING PROCESS DOCUMENTATION
 * 
 * AGC: When a potion is successfully crafted, it must be minted ON-CHAIN with rarity:
 * 
 * 1. User crafts potion using materials (deducted from inventory)
 * 2. Backend calls contract's mintPotion() or mintPotionWithRarity() function
 * 3. Contract rolls rarity ON-CHAIN using rollRarity(farmPro) - same as classic potions
 * 4. Contract calculates magnitude = (baseMagnitude * rarityMultiplier) / 100
 * 5. Contract stores potionMetadata on-chain with:
 *    - potionTypeId (mapped from PotionDefinition.id)
 *    - rarity (rolled on-chain)
 *    - magnitude (calculated with rarity)
 *    - mintedAt (block.timestamp)
 * 
 * AGC: RARITY ROLLING (ON-CHAIN):
 * - Uses same rollRarity(farmPro) function as ClassicPotion1155
 * - FarmPro score (0-100) affects rarity chances:
 *   - Base: Uncommon 70%, Rare 30%, Epic 10%, Legendary 1%
 *   - Target (FarmPro=100): Uncommon 98%, Rare 55%, Epic 20%, Legendary 3%
 * - Linear interpolation based on FarmPro value
 * 
 * AGC: RARITY MULTIPLIERS:
 * - Common: 1.00× (100)
 * - Uncommon: 1.10× (110)
 * - Rare: 1.20× (120)
 * - Epic: 1.50× (150) - updated 2025-11-27
 * - Legendary: 2.00× (200) - updated 2025-11-27
 * 
 * AGC: EXAMPLE:
 * - Minor Haste Potion (base: 2.5 dmg)
 * - Rolled rarity: Epic (1.50×)
 * - Final magnitude: (2.5 * 150) / 100 = 3.75 dmg
 * 
 * AGC: NOTE: This TypeScript module provides calculation helpers.
 * AGC: Actual minting and rarity rolling happens ON-CHAIN in the contract.
 * AGC: Frontend/backend should call contract functions, not these helpers for minting.
 */

